/**
 * @fileoverview Past Events Controller Module.
 * Manages interactions and functionalities related to the past events section on the page.
 * This includes event filtering, pagination, and dynamic content loading.
 */


import { elementExists, adjustTitleHeights, closeAllDropdowns, getAjaxUrl } from "../Utils";

const PastEventsController = {
    eventsContainer: '.past-events-js',
    desktopSelectedEventClass: 'border-b-4',
    eventsContainerId: 'events-container-past',
    seeMoreUpcomingEventsSelector: '.see-more-upcoming-events-js',
    defaultFiltersLabel: 'All events',
    filtersLabel: '.dropdown-label-js',

    /**
     * Initializes the PastEventsController.
     * Adjusts title heights, sets listeners, and configures pagination for past events.
     */
    init() {
        if (!elementExists(this.eventsContainer)) return false;
        adjustTitleHeights();
        this.setListeners();
        this.setPagination();
    },

    /**
     * Sets up event listeners for filter links and pagination buttons.
     */
    setListeners() {
        const filters = document.querySelectorAll("[data-slug]");
        filters.forEach(link => {
            link.addEventListener('click', (ev) => this.clickFilterHandler(ev, filters));
        });
    },

    /**
     * Configures the pagination buttons and their visibility based on page count.
     */
    setPagination() {
        const paginationContainer = document.querySelector('.pagination-container-js');
        if (!paginationContainer) return;

        const maxPages = this.getMaxPages();
        this.paginationButtonActions(maxPages);
    },

    /**
     * Updates the state of pagination buttons based on current and maximum pages.
     * @param {number} maxPages - The maximum number of pages available.
     */
    paginationButtonActions(maxPages) {
        const currentPage = this.getPageNumberFromUrl();
        const previousButton = document.querySelector('.previous-page-js');
        const nextButton = document.querySelector('.next-page-js');
        if (!currentPage) {
            this.makeInvisible(previousButton);
            this.makeVisible(nextButton);
        } else {
            if (currentPage === maxPages) {
                this.makeInvisible(nextButton);
                this.makeVisible(previousButton);
            }
        }
    },

    /**
     * Makes the provided element invisible.
     * @param {HTMLElement} el - The element to make invisible.
     */
    makeInvisible(el) {
        if (!el) return
        el.classList.add('invisible');
    },

    /**
     * Makes the provided element visible.
     * @param {HTMLElement} el - The element to make visible.
     */
    makeVisible(el) {
        if (!el) return
        el.classList.remove('invisible');
    },

    /**
     * Retrieves the current page number from the URL.
     * @returns {number|null} The current page number or null if not found.
     */
    getPageNumberFromUrl() {
        const url = window.location.href;
        const match = url.match(/\/page\/(\d+)/);
        return match ? match[1] : null;
    },

    /**
     * Retrieves the maximum number of pages from the DOM.
     * @returns {number} The maximum number of pages.
     */
    getMaxPages() {
        return document.querySelector('.max-pages-js')?.textContent.trim();
    },

    /**
     * Event handler for filter link clicks.
     * @param {Event} ev - The event object.
     * @param {NodeList} filters - Collection of filter elements.
     */
    clickFilterHandler(ev, filters) {
        ev.preventDefault();
        let slug = ev.srcElement.getAttribute('data-slug');
        this.setActiveFilter(ev, filters, slug);
        this.updateMobileFilterLabel(slug);
        this.fetchFilteredEvents(slug);
        this.addParamToUrl(slug);
    },

    /**
     * Sets an active filter based on the clicked filter element.
     * @param {Event} ev - The event object.
     * @param {NodeList} filters - Collection of filter elements.
     * @param {string} slug - The data attribute representing the filter.
     */
    setActiveFilter(ev, filters, slug) {
        filters.forEach(link => {
            const itemSlug = link.getAttribute('data-slug');
            if (link.classList.contains('desktop-js')) {
                if (itemSlug === slug) {
                    link.parentElement.classList.add(this.desktopSelectedEventClass);
                } else {
                    link.parentElement.classList.remove(this.desktopSelectedEventClass);
                }
            } else if (link.classList.contains('mobile-js')) {
                if (itemSlug === slug) {
                    link.classList.add('selected');
                } else {
                    link.classList.remove('selected');
                }
            }
        });
        closeAllDropdowns();
    },

    /**
     * Updates the label for the mobile filter dropdown based on the selected filter.
     * @param {string} slug - The data attribute representing the filter.
     */
    updateMobileFilterLabel(slug) {
        const dropdownLabelElement = document.querySelector(this.filtersLabel);
        if (!dropdownLabelElement) return;
        if (slug === 'all') {
            dropdownLabelElement.textContent = this.defaultFiltersLabel;
        } else {
            dropdownLabelElement.textContent = slug;
        }
    },

    /**
     * Fetches events based on the provided slug (event type).
     * Makes an asynchronous request to retrieve filtered events data.
     * @param {string} slug - The event type to filter the events.
     */
    fetchFilteredEvents(slug) {
        let data = new FormData();
        data.append('action', 'fetch_filtered_past_events');
        data.append('event_type', slug);

        fetch(getAjaxUrl(), {
            method: 'POST',
            body: data
        })
            .then(response => response.json())
            .then(data => this.fetchFilteredEventsSuccess(data, slug))
            .catch(error => console.error('Error fetching events:', error));
    },

    /**
     * Adds a parameter to the URL without reloading the page.
     * @param {string} slug - The value to be set for the 'event_type' URL parameter.
     */
    addParamToUrl(slug) {
        let currentURL = new URL(window.location.href);
        let searchParams = currentURL.searchParams;
        searchParams.set('event_type', slug);
        window.history.pushState({}, '', currentURL.toString());
    },

    /**
     * Handles the successful response from the fetchFilteredEvents function.
     * @param {Object} data - The data returned from the fetch request.
     * @param {string} slug - The current event type filter.
     */
    fetchFilteredEventsSuccess(data, slug) {
        let { events, hasMore, maxNumPages } = data;
        events = events.filter(event => event !== null);
        const eventsContainer = document.getElementById(this.eventsContainerId);
        eventsContainer.innerHTML = '';
        events.forEach(event => {
            let eventDiv = document.createElement('div');
            let endDateHtml = event.end_date ? `
                                <div class="pl-5 border-l border-pxl-white-25">
                                    <div class="font-semibold text-heading-medium">
                                        ${event.end_date}
                                    </div>
                                    <div class="opacity-80">
                                        End Date
                                    </div>
                                </div>
                            ` : '';
            eventDiv.innerHTML = `
                <a href="${event.permalink}" class="content-card-link">
                    <span class="top-cnt">
                        <span class="img-wrap" style="background-image: url('${event.featured_image}');"> 
                            <span class="tags-wrap">
                                <span class="tag">${event.event_type}</span>
                                <!-- .tag -->
                            </span>
                            <!-- .tags-wrap -->
                        </span>
                        <!-- .img-wrap -->
                    </span>
                    <!-- .top-cnt -->
                
                    <span class="bottom-cnt">
                        <h3>${event.title}</h3>
                        <span class="card-meta">
                            <span class="meta meta-date">${event.start_date}</span>
                        </span>
                        <p>${event.excerpt}</p>
                    </span>
                    <!-- .bottom-cnt -->
                </a>
                `;
            eventsContainer.appendChild(eventDiv);
            this.seeMoreButtonActions(hasMore);
        });
        this.resetPaginationToDefault(maxNumPages, slug);
    },

    /**
     * Resets pagination to its default state and updates the URL.
     * @param {number} maxNumPages - The maximum number of pages.
     * @param {string} slug - The current event type filter.
     */
    resetPaginationToDefault(maxNumPages, slug) {
        const paginationContainer = document.querySelector('.pagination-container-js');
        if (maxNumPages <= 1) {
            paginationContainer.classList.add('hidden');
        } else {
            paginationContainer.classList.remove('hidden');
        }

        this.resetUrl();
        const previousButton = document.querySelector('.previous-page-js');
        const nextButton = document.querySelector('.next-page-js');

        if (previousButton) {
            previousButton.setAttribute("href", `/past-events${slug ? '?event_type=' + slug : ''}`);
        }

        if (nextButton) {
            nextButton.setAttribute("href", `/past-events/page/2${slug ? '?event_type=' + slug : ''}`);
        }

        const currentPage = document.querySelector('.current-page-js');
        if (currentPage) {
            currentPage.textContent = '1';
        }

        const maxPage = document.querySelector('.max-pages-js');
        if (maxPage) {
            maxPage.textContent = maxNumPages;
        }

        this.paginationButtonActions(maxNumPages);
    },

    /**
     * Resets the URL to its default state, removing any page parameters.
     */
    resetUrl() {
        let currentUrl = window.location.href;
        let newUrl = currentUrl.replace(/\/page\/\d+\//, '/');

        history.replaceState(null, null, newUrl);
    },

    /**
     * Updates the visibility of the 'See More' button based on the availability of more events.
     * @param {boolean} hasMore - Indicates if more events are available to load.
     */
    seeMoreButtonActions(hasMore) {
        const seeMoreButton = document.querySelectorAll(this.seeMoreUpcomingEventsSelector)[0];
        if (seeMoreButton) {
            seeMoreButton.setAttribute('data-page', '2');
            if (hasMore) {
                seeMoreButton.classList.remove('hidden');
            } else {
                seeMoreButton.classList.add('hidden');
            }
        }
    },
};

export default PastEventsController;